import type * as Blockly from 'blockly/core';
import { BOARD_CONFIG } from '@/(routers)/boards/editor/block/blockConfig';
import {
    expr,
    registerHelperFunction,
    registerSetupCode,
    resolveBoard,
} from '@/(routers)/boards/editor/block/generators/arduino/_gen';
import {
    GPS_BASE_HELPER_CODE,
    GPS_BASE_HELPER_CODE_BLUE,
    GPS_POSITION_LABELS,
    gpsGlobalCode,
    gpsGlobalCodeBlue,
    gpsGlobalCodes,
    markGpsUsed,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/gps/helper/gps_helper';

type Position = keyof (typeof BOARD_CONFIG)[keyof typeof BOARD_CONFIG]['pins'];

const DAYS_IN_MONTH_HELPER_CODE = `int _sbeGpsDaysInMonth(int year, int month) {
  if (month == 2) {
    return (year % 4 == 0 && (year % 100 != 0 || year % 400 == 0)) ? 29 : 28;
  }
  if (month == 4 || month == 6 || month == 9 || month == 11) return 30;
  return 31;
}`;

function buildTimeHelperCode(format: string): string {
    const formatMap: Record<string, string> = {
        DATETIME: `snprintf(buf, sizeof(buf), "%04d/%02d/%02d %02d:%02d:%02d", y, mo, d, h, m, s);`,
        DATE: `snprintf(buf, sizeof(buf), "%04d/%02d/%02d", y, mo, d);`,
        TIME: `snprintf(buf, sizeof(buf), "%02d:%02d:%02d", h, m, s);`,
    };

    const safeFormat = format in formatMap ? format : 'DATETIME';
    const snprintf = formatMap[safeFormat];

    return `String _sbeGpsGetTime_${safeFormat}(TinyGPSPlus* gps, SoftwareSerial* serial, bool* warned, const char* label) {
  _sbeGpsCheckAge(gps, serial, warned, label);
  if (!gps->time.isValid() || !gps->date.isValid()) return "--";
  int h = (gps->time.hour() + 9) % 24;
  int m = gps->time.minute();
  int s = gps->time.second();
  int y = gps->date.year();
  int mo = gps->date.month();
  int d = gps->date.day();
  if (gps->time.hour() + 9 >= 24) {
    d += 1;
    if (d > _sbeGpsDaysInMonth(y, mo)) {
      d = 1;
      mo += 1;
      if (mo > 12) {
        mo = 1;
        y += 1;
      }
    }
  }
  char buf[20];
  ${snprintf}
  return String(buf);
}`;
}

function buildTimeHelperCodeBlue(format: string): string {
    const formatMap: Record<string, string> = {
        DATETIME: `snprintf(buf, sizeof(buf), "%04d/%02d/%02d %02d:%02d:%02d", y, mo, d, h, m, s);`,
        DATE: `snprintf(buf, sizeof(buf), "%04d/%02d/%02d", y, mo, d);`,
        TIME: `snprintf(buf, sizeof(buf), "%02d:%02d:%02d", h, m, s);`,
    };

    const safeFormat = format in formatMap ? format : 'DATETIME';
    const snprintf = formatMap[safeFormat];

    return `String _sbeGpsGetTime_blue_${safeFormat}(TinyGPSPlus* gps, bool* warned, const char* label) {
  _sbeGpsCheckAge_blue(gps, warned, label);
  if (!gps->time.isValid() || !gps->date.isValid()) return "--";
  int h = (gps->time.hour() + 9) % 24;
  int m = gps->time.minute();
  int s = gps->time.second();
  int y = gps->date.year();
  int mo = gps->date.month();
  int d = gps->date.day();
  if (gps->time.hour() + 9 >= 24) {
    d += 1;
    if (d > _sbeGpsDaysInMonth(y, mo)) {
      d = 1;
      mo += 1;
      if (mo > 12) {
        mo = 1;
        y += 1;
      }
    }
  }
  char buf[20];
  ${snprintf}
  return String(buf);
}`;
}

export function register(gen: any): void {
    gen.forBlock['gps_datetime'] = function (block: Blockly.Block): [string, number] {
        const position = block.getFieldValue('POSITION') as Position;
        const format = block.getFieldValue('FORMAT') as string;
        const board = resolveBoard(block);
        const pins = BOARD_CONFIG[board].pins[position];
        const rx = pins.second;
        const tx = pins.first;
        const label = GPS_POSITION_LABELS[position] ?? position;

        markGpsUsed();

        if (board === 'blue') {
            gpsGlobalCodes.set(`${tx}`, gpsGlobalCodeBlue(tx));
            registerHelperFunction('gps_base', GPS_BASE_HELPER_CODE_BLUE);
            registerHelperFunction('gps_days_in_month', DAYS_IN_MONTH_HELPER_CODE);
            registerHelperFunction(`gps_time_blue_${format}`, buildTimeHelperCodeBlue(format));
            registerSetupCode(
                `gps_${tx}`,
                `  Serial2.begin(9600, SERIAL_8N1, ${rx}, ${tx});\n  _sbeGpsBegin_blue("${label}");\n`,
            );
            return expr(`_sbeGpsGetTime_blue_${format}(&_sbeGps_${tx}, &_sbeGpsWarned_${tx}, "${label}")`);
        }

        gpsGlobalCodes.set(`${tx}`, gpsGlobalCode(rx, tx));
        registerHelperFunction('gps_base', GPS_BASE_HELPER_CODE);
        registerHelperFunction('gps_days_in_month', DAYS_IN_MONTH_HELPER_CODE);
        registerHelperFunction(`gps_time_${format}`, buildTimeHelperCode(format));
        registerSetupCode(`gps_${tx}`, `  _sbeGpsBegin(&_sbeGpsSerial_${tx}, "${label}");\n`);
        return expr(`_sbeGpsGetTime_${format}(&_sbeGps_${tx}, &_sbeGpsSerial_${tx}, &_sbeGpsWarned_${tx}, "${label}")`);
    };
}
